// src/pages/buyer-sales/list.tsx
import { ReactNode, useCallback, useEffect, useMemo, useState } from "react"
import { Box, Button, Checkbox, Group, Modal, Popover, Stack, Text, TextInput } from "@mantine/core"
import { ChecksIcon, ColumnsIcon, MagnifyingGlassIcon, PaperPlaneTiltIcon, PlusIcon, WarningIcon, XIcon } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next"
import { DisabledReason, EmptyStateGuide, HelpButtonGroup, HelpIcon, useHelpContext } from "@dehub/theme"
import { useNavigate } from "react-router"
import { useSelector } from "react-redux"
import { OrderApi } from "../../apis"
import { PageLayout } from "../../components/layout"
import { usePermission } from "../../components/permission"
import { BuyerBulkActionForm, type BuyerBulkKind } from "../../components/buyer-sales/bulk-action-form"
import { useSalesActions, type SalesAction } from "../../components/order/order-actions"
import { BuyerSalesPaymentForm } from "../../components/buyer-sales/payment-form"
import { BuyerSalesDynamicReviewForm } from "../../components/buyer-sales/review-form"
import { BuyerSalesDynamicConfirmForm } from "../../components/buyer-sales/confirm-form"
import { orderSupplier, useReorder } from "../../components/buyer-sales/reorder"
import { OrderTable, useOrderList, useOrderFilters, useOrderSelection, type OrderTableColumns } from "../../components/buyer-sales-list"
import { TableFrame } from "../../components/delivery/atoms"
import { translateBackendError } from "../../components/delivery/translate-error"
import { BulkActionBar, type BulkAction, ListState, SegmentedTabs } from "../../components/ui"
import { IOrderPlus } from "../../models/Order"
import { IAuth } from "../../store/Auth"
import { Dialog } from "../../utils/confirm-modal"
import { message } from "../../utils/message"
import { renderBusinessName } from "../../utils/render-business-name"
import classes from "./list.module.css"

/** Client-side search reads this many of the newest orders (the list endpoint has no text filter). */
const SEARCH_WINDOW = 200
const COLUMNS_KEY = "buyer-sales.columns"

/** Bulk kind -> the action-matrix action (order-actions.ts) whose rule and permission it follows. */
const BULK_ACTION: Record<BuyerBulkKind, SalesAction> = { approve: "approve", reject: "decline", cancel: "cancel", review: "review" }

type Chip = { key: string; orderStatus: string; deliveryNoteStatus: string }

// B54: «Зөвшөөрсөн» finds confirmed orders (AUTHORIZED). APPROVED only marks an
// order still waiting for its advance payment, which DEV/PROD hardly ever hold.
const CHIPS: Chip[] = [
  { key: "all", orderStatus: "", deliveryNoteStatus: "" },
  { key: "REVIEWED", orderStatus: "REVIEWED", deliveryNoteStatus: "" },
  { key: "AUTHORIZED", orderStatus: "AUTHORIZED", deliveryNoteStatus: "" },
  { key: "DELIVERING", orderStatus: "", deliveryNoteStatus: "DELIVERING" },
  { key: "DELIVERED", orderStatus: "", deliveryNoteStatus: "DELIVERED" },
]

const loadColumns = (): OrderTableColumns => {
  try {
    const raw = localStorage.getItem(COLUMNS_KEY)
    if (raw) return { term: true, invoice: true, ...JSON.parse(raw) }
  } catch {
    /* default columns */
  }
  return { term: true, invoice: true }
}

const matchesSearch = (order: IOrderPlus, query: string) => {
  const q = query.trim().toLowerCase()
  if (!q) return true
  const supplier = orderSupplier(order)
  return [
    order.salesCode,
    order.purchaseCode,
    order.invoice?.refCode,
    supplier?.profileName,
    supplier?.partner?.businessName,
    renderBusinessName(supplier, ""),
    supplier?.regNumber,
  ].some((v) => typeof v === "string" && v.toLowerCase().includes(q))
}

export function BuyerSalesList() {
  const { t } = useTranslation(["order", "common"])
  const navigate = useNavigate()
  const { user } = useSelector((state: { auth: IAuth }) => state.auth)

  const ORD_LIST = usePermission("ORD_LIST")
  const ORD_PO_LIST = usePermission("ORD_PO_LIST")
  const ORD_SO_DETAIL = usePermission("ORD_SO_DETAIL")
  const ORD_CANCEL = usePermission("ORD_CANCEL")
  const ORD_SAVE = usePermission("ORD_SAVE")
  const ORD_SEND = usePermission("ORD_SEND")
  const canList = ORD_LIST.hasAccess || ORD_PO_LIST.hasAccess
  const canCreate = ORD_SAVE.hasAccess || ORD_SEND.hasAccess

  const { filters, debouncedFilters, updateFilter, clearFilters, setPage } = useOrderFilters()
  const searching = !!debouncedFilters.search?.trim()
  const query = searching ? { ...debouncedFilters, page: 1, limit: SEARCH_WINDOW } : debouncedFilters
  const { orders, total, isLoading, error, refresh } = useOrderList(query, canList)
  const visible = useMemo(
    () => (searching ? orders.filter((o: IOrderPlus) => matchesSearch(o, debouncedFilters.search || "")) : orders),
    [orders, searching, debouncedFilters.search],
  )

  const { stateOf, permissionState } = useSalesActions()
  const selection = useOrderSelection()
  const [bulk, setBulk] = useState<BuyerBulkKind | null>(null)
  const [bulkBusy, setBulkBusy] = useState(false)

  // The row's workflow actions that need no per-row input (payment, edit and reorder are not bulk).
  const appliesTo = useCallback(
    (kind: BuyerBulkKind, order: IOrderPlus) => stateOf(BULK_ACTION[kind], order).applies,
    [stateOf],
  )
  const isSelectable = useCallback(
    (order: IOrderPlus) => (["approve", "reject", "cancel", "review"] as BuyerBulkKind[]).some((k) => appliesTo(k, order)),
    [appliesTo],
  )
  // Only rows of the current page/search that are still selectable count.
  const selectedRows = useMemo(
    () => visible.filter((o: IOrderPlus) => o.id && selection.selectedIds.has(o.id) && isSelectable(o)),
    [visible, selection.selectedIds, isSelectable],
  )
  const toggleAll = () => {
    const selectable = visible.filter((o: IOrderPlus) => o.id && isSelectable(o))
    if (selectable.length > 0 && selectedRows.length === selectable.length) selection.clearSelection()
    else selection.selectAll(selectable.map((o: IOrderPlus) => o.id!))
  }
  const bulkAction = (kind: BuyerBulkKind, label: string, icon: ReactNode, primary?: boolean): BulkAction => {
    const rows = selectedRows.filter((o: IOrderPlus) => appliesTo(kind, o))
    const perm = permissionState(BULK_ACTION[kind])
    return {
      key: kind,
      label,
      icon,
      primary,
      reason: perm.reason ?? (rows.length === 0 ? t("order:bulk.noApplicable") : undefined),
      // @dehub/help: attached «?» (R2) + reason popover with a plain-language code (R4).
      helpKey: `order.buyer-sales-list.action.bulk-${kind}`,
      reasonHelpKey: "order.buyer-sales-list.reason.bulk",
      reasonCode: perm.reason ? `PERM_${kind.toUpperCase()}` : rows.length === 0 ? "NONE_IN_STATUS" : null,
      helpId: `bulk-${kind}`,
      onClick: () => {
        if (perm.allowed && rows.length > 0) setBulk(kind)
      },
    }
  }
  const bulkOrders = bulk ? selectedRows.filter((o: IOrderPlus) => appliesTo(bulk, o)) : []

  const { reorderById } = useReorder()
  const [reorderingId, setReorderingId] = useState<string | null>(null)
  const [action, setAction] = useState<string[]>([])
  const [columns, setColumns] = useState<OrderTableColumns>(loadColumns)

  const toggleColumn = (key: keyof OrderTableColumns) => {
    setColumns((prev) => {
      const next = { ...prev, [key]: !prev[key] }
      try {
        localStorage.setItem(COLUMNS_KEY, JSON.stringify(next))
      } catch {
        /* per-viewer convenience only */
      }
      return next
    })
  }

  const activeChip = CHIPS.find((c) => c.orderStatus === (filters.orderStatus || "") && c.deliveryNoteStatus === (filters.deliveryNoteStatus || ""))?.key ?? "all"
  const applyChip = (chip: Chip) => {
    updateFilter("orderStatus", chip.orderStatus)
    updateFilter("deliveryNoteStatus", chip.deliveryNoteStatus)
  }
  const hasFilter = activeChip !== "all" || searching

  // @dehub/help: the buyer list mixes both sides, so `side` stays unset (BUYER status rows apply); the «Одоо»
  // cards count the loaded rows that wait on the buyer, {{count}} feeds the bulk-bar «?».
  const myBusinessId = user?.currentBusinessId ?? user?.currentBusiness?.id
  const helpVars = useMemo(() => {
    const isMine = (o: IOrderPlus) => (myBusinessId ? myBusinessId === o.senderBusiness?.id : o.type === "PURCHASE")
    const isTheirs = (o: IOrderPlus) => (myBusinessId ? myBusinessId === o.receiverBusiness?.id : o.type === "SALES")
    const unpaid = (o: IOrderPlus) => !!o.proformaInvoiceId && !(o.invoice?.paymentStatus === "CLOSED" || o.invoice?.invoiceStatus === "CLOSED")
    return {
      answerCount: visible.filter((o: IOrderPlus) => o.orderStatus === "REVIEWED" && isTheirs(o)).length,
      reviewCount: visible.filter((o: IOrderPlus) => o.orderStatus === "REGISTERED" && isMine(o)).length,
      payCount: visible.filter((o: IOrderPlus) => o.orderStatus === "APPROVED" && unpaid(o)).length,
      count: selectedRows.length,
    }
  }, [visible, selectedRows.length, myBusinessId])
  const { set: setHelpContext } = useHelpContext()
  useEffect(() => {
    setHelpContext({ vars: helpVars })
  }, [helpVars, setHelpContext])

  const handleRowAction = (actionType: string, order: IOrderPlus) => {
    switch (actionType) {
      case "detail":
        if (ORD_SO_DETAIL.hasAccess) navigate(`/buyer-sales/detail/${order.id}`)
        break
      case "edit":
        navigate(`/buyer-sales/edit/${order.id}`)
        break
      case "reorder":
        if (!order.id) break
        setReorderingId(order.id)
        reorderById(order.id).finally(() => setReorderingId(null))
        break
      case "payment":
        setAction(["PAYMENT", order as any])
        break
      case "review":
        setAction(["reviewed", order as any])
        break
      case "approve":
        setAction(["confirm", order as any, "confirm"])
        break
      case "reject":
        setAction(["reject", order as any, "reject"])
        break
      case "cancel":
        Dialog.confirm(t("buyerSales.cancelConfirm"), async (key) => {
          if (key === "confirm" && order.id) {
            try {
              await OrderApi.cancel(order.id)
              message.success(t("buyerSales.cancelSuccess"))
              refresh()
            } catch (err) {
              message.error(translateBackendError(err))
            }
          }
        })
        break
    }
  }

  const reload = async () => {
    refresh()
  }

  const totalCount = searching ? visible.length : total

  const columnPicker = (
    <Popover key="columns" position="bottom-end" width={300} shadow="md" radius="md">
      <Popover.Target>
        <Button variant="default" leftSection={<ColumnsIcon size={16} />}>
          {t("order:buyerOrders.pickColumns")}
        </Button>
      </Popover.Target>
      <Popover.Dropdown>
        <Stack gap="sm">
          <Text fw={600} fz="var(--nw-fz-body)">{t("order:buyerOrders.visibleColumns")}</Text>
          <Checkbox label={t("order:buyerSales.paymentTerm")} checked={columns.term} onChange={() => toggleColumn("term")} />
          <Checkbox label={t("order:columns.invoice")} checked={columns.invoice} onChange={() => toggleColumn("invoice")} />
          <Text className={classes.hint}>{t("order:buyerOrders.coreColumnsHint")}</Text>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  )

  const body = () => {
    if (!canList) return <ListState state="denied" permission="ORD_LIST / ORD_PO_LIST" title={t("order:buyerOrders.deniedTitle")} />
    if (isLoading) return <TableFrame><ListState state="loading" columns={8} /></TableFrame>
    if (error) {
      return (
        <TableFrame>
          <ListState state="error" title={t("order:buyerOrders.errorTitle")} onRetry={() => refresh()} errorCode={(error as { code?: string | number })?.code} />
        </TableFrame>
      )
    }
    if (visible.length === 0) {
      // @dehub/help empty state (R10: the locale ListState stays as the fallback).
      const fallback = hasFilter ? (
        <ListState
          state="noResults"
          title={searching ? t("order:buyerOrders.noSearchResults", { query: debouncedFilters.search }) : t("order:buyerOrders.noStatusResults")}
          action={{ onClick: clearFilters }}
        />
      ) : (
        <ListState
          state="empty"
          title={t("order:buyerOrders.emptyTitle")}
          description={t("order:buyerOrders.emptyText")}
          action={canCreate ? { label: t("buyerSales.newOrder"), onClick: () => navigate("/buyer-sales/new") } : undefined}
        />
      )
      return (
        <TableFrame>
          <EmptyStateGuide
            helpKey="order.buyer-sales-list.empty.main"
            when={searching ? "noResults" : hasFilter ? "status" : "empty"}
            actions={{ clear: clearFilters, ...(canCreate ? { create: () => navigate("/buyer-sales/new") } : {}) }}
            fallback={fallback}
          />
        </TableFrame>
      )
    }
    return (
      <OrderTable
        orders={visible}
        columns={columns}
        page={filters.page}
        limit={filters.limit}
        total={totalCount}
        paginate={!searching}
        onPageChange={setPage}
        onRowAction={handleRowAction}
        permissions={{
          canDetail: ORD_SO_DETAIL.hasAccess,
          canEdit: canCreate,
          canCancel: ORD_CANCEL.hasAccess,
          canReorder: canCreate,
        }}
        currentUserId={user?.currentBusinessId ?? user?.currentBusiness?.id}
        reorderingId={reorderingId}
        select={{ isSelectable, selectedIds: selection.selectedIds, onToggle: selection.toggleSelection, onToggleAll: toggleAll }}
      />
    )
  }

  return (
    <>
      <PageLayout
        title={t("order:buyerOrders.title")}
        extra={[
          columnPicker,
          // Primary entry point: «?» explains the catalogue flow (R2); missing permission → reason popover (R4).
          <DisabledReason key="new-order" helpKey="order.buyer-sales-list.reason.new" reason="PERM" when={!canCreate} position="bottom-end">
            <HelpButtonGroup helpKey="order.buyer-sales-list.action.new">
              <Button data-help-id="new-order" onClick={() => navigate("/buyer-sales/new")} leftSection={<PlusIcon size={16} />} color="brand">
                {t("buyerSales.newOrder")}
              </Button>
            </HelpButtonGroup>
          </DisabledReason>,
        ]}
      >
        <Group justify="space-between" wrap="wrap" gap="sm">
          {/* One list-header concept (segmented tabs · «?»), shared with the supplier lists and de-network-admin. */}
          <Group gap="xs" wrap="nowrap" aria-label={t("order:columns.status")}>
            <SegmentedTabs
              value={activeChip}
              onChange={(value) => {
                const chip = CHIPS.find((c) => c.key === value)
                if (chip) applyChip(chip)
              }}
              data={CHIPS.map((chip) => ({ value: chip.key, label: t(`order:buyerOrders.chips.${chip.key}`) }))}
            />
            <HelpIcon helpKey="order.buyer-sales-list.types.status-tabs" />
          </Group>
          <Group gap="sm" wrap="nowrap" className={classes.searchGroup}>
            <Text className={classes.total}>{t("buyerSales.subtitle", { total: totalCount })}</Text>
            <TextInput
              className={classes.search}
              value={filters.search || ""}
              onChange={(e) => updateFilter("search", e.currentTarget.value)}
              placeholder={t("order:buyerOrders.searchPlaceholder")}
              leftSection={<MagnifyingGlassIcon size={16} />}
              aria-label={t("order:buyerOrders.searchPlaceholder")}
            />
          </Group>
        </Group>

        {searching && <Text className={classes.hint}>{t("order:buyerOrders.searchWindowHint", { count: SEARCH_WINDOW })}</Text>}

        <Box>{body()}</Box>

        <Text className={classes.hint}>{t("order:buyerOrders.cancelFootnote")}</Text>
      </PageLayout>

      <Modal
        opened={action[0] === "PAYMENT"}
        onClose={() => setAction([])}
        size={1240}
        withCloseButton={false}
        padding={0}
        centered
        radius="lg"
      >
        <BuyerSalesPaymentForm action={action} setAction={setAction} reload={reload} />
      </Modal>

      <Modal
        opened={action[0] === "reviewed"}
        onClose={() => setAction([])}
        size="600px"
        withCloseButton={false}
        padding={0}
        centered
        radius="lg"
      >
        <BuyerSalesDynamicReviewForm action={action && action[1]} setActions={setAction} reload={reload} />
      </Modal>

      <Modal
        opened={action[0] === "confirm" || action[0] === "reject"}
        onClose={() => setAction([])}
        size="600px"
        withCloseButton={false}
        padding={0}
        centered
        radius="lg"
      >
        <BuyerSalesDynamicConfirmForm
          action={action && action[1]}
          setActions={setAction}
          constant={action && action[2]}
          reload={reload}
        />
      </Modal>

      <Modal opened={bulk !== null} onClose={() => !bulkBusy && setBulk(null)} closeOnEscape={!bulkBusy} closeOnClickOutside={!bulkBusy} withCloseButton={false} size="600px" padding={0} centered radius="lg">
        {bulk && (
          <BuyerBulkActionForm
            kind={bulk}
            orders={bulkOrders}
            onCancel={() => setBulk(null)}
            onBusyChange={setBulkBusy}
            reload={reload}
            clearSelection={selection.clearSelection}
          />
        )}
      </Modal>

      {/* The row's actions, applied to every selected order (as in de-invoice-admin). */}
      <BulkActionBar
        selectedCount={selectedRows.length}
        onClear={selection.clearSelection}
        actions={[
          bulkAction("cancel", t("common:actions.cancel"), <XIcon size={16} />),
          bulkAction("reject", t("order:actions.reject"), <WarningIcon size={16} />),
          bulkAction("review", t("order:actions.review"), <PaperPlaneTiltIcon size={16} />),
          bulkAction("approve", t("order:actions.approve"), <ChecksIcon size={16} />, true),
        ]}
      />
    </>
  )
}
